import { useDebugStore } from '@/lib/store/debug';
import { type MouseEvent, useCallback } from 'react';

// 'sidebar' resizer sits on the LEFT edge of the right-docked aside, so dragging
// left widens it (inverted delta). 'tree'/'messages' are normal left→right
// column splits inside the panel.
type Target = 'sidebar' | 'tree' | 'messages';
const BOUNDS: Record<Target, { min: number; max: number }> = {
  sidebar: { min: 360, max: 1100 },
  tree: { min: 140, max: 480 },
  messages: { min: 160, max: 520 },
};

export function useDebugPaneResize(target: Target) {
  const setSidebarWidth = useDebugStore((s) => s.setSidebarWidth);
  const setPaneWidth = useDebugStore((s) => s.setPaneWidth);
  return useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const st = useDebugStore.getState();
      const startW = target === 'sidebar' ? st.sidebarWidth : st.paneWidths[target];
      const { min, max } = BOUNDS[target];
      const onMove = (ev: globalThis.MouseEvent) => {
        const delta = target === 'sidebar' ? startX - ev.clientX : ev.clientX - startX;
        const next = Math.min(max, Math.max(min, startW + delta));
        if (target === 'sidebar') setSidebarWidth(next);
        else setPaneWidth(target, next);
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [target, setSidebarWidth, setPaneWidth],
  );
}
